import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LandingPage from "./LandingPage";
import GameInterface from "./GameInterface";
import EndGamePage from "./EndGamePage";

export const difficultySettings = {
  easy: { pointsPerQuestion: 1 },
  medium: { pointsPerQuestion: 2 },
  hard: { pointsPerQuestion: 3 },
};

function App() {
  const [gamePhase, setGamePhase] = useState("landing");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);

  useEffect(() => {
    fetch("/trivia.json")
      .then((res) => res.json())
      .then((data) => {
        const filtered = data
          .filter((q) => q.id !== 100000)
          .map((q) => ({
            question: q.question,
            answer: q.answer,
            category: q.category,
            difficulty: q.difficulty,
          }));
        setQuestions(filtered);

        const uniqueCategories = [
          ...new Set(filtered.map((q) => q.category)),
        ];
        setCategories(uniqueCategories);
      })
      .catch((err) => console.error("Error loading questions:", err));
  }, []);

  useEffect(() => {
    if (
      questions.length > 0 &&
      selectedCategories.length > 0 &&
      selectedDifficulty.length > 0
    ) {
      const filteredQuestions = questions.filter(
        (question) =>
          selectedCategories.includes(question.category) &&
          selectedDifficulty.includes(question.difficulty)
      );
      setShuffledQuestions(shuffleArray(filteredQuestions));
    }
  }, [questions, selectedCategories, selectedDifficulty]);

  const handleAnswer = () => {
    setTimeout(() => {
      if (currentQuestion === shuffledQuestions.length - 1) {
        setGamePhase("end");
      } else {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const nextQuestion = () => {
    if (currentQuestion < shuffledQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      setGamePhase("end");
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  function shuffleArray(array) {
    let shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  const gameActions = {
    resetGame: () => {
      setGamePhase("landing");
      setSelectedCategories([]);
      setSelectedDifficulty([]);
      setCurrentQuestion(0);
    },
    startGame: () => setGamePhase("category"),
    selectCategory: (category) => {
      setSelectedCategories((prev) =>
        prev.includes(category)
          ? prev.filter((cat) => cat !== category)
          : [...prev, category]
      );
    },
    selectDifficulty: (difficulty) => {
      setSelectedDifficulty((prev) =>
        prev.includes(difficulty)
          ? prev.filter((diff) => diff !== difficulty)
          : [...prev, difficulty]
      );
    },
    toggleAllCategories: () => {
      setSelectedCategories((prev) =>
        prev.length === categories.length ? [] : [...categories]
      );
    },
    toggleAllDifficulties: () => {
      const allDiffs = Object.keys(difficultySettings);
      setSelectedDifficulty((prev) =>
        prev.length === allDiffs.length ? [] : allDiffs
      );
    },
    setGamePhase,
    nextQuestion,
    previousQuestion,
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            gamePhase === "landing" ? (
              <LandingPage
                resetSelections={gameActions.resetGame}
                onStart={() => setGamePhase("category")}
              />
            ) : gamePhase === "end" ? (
              <EndGamePage resetGame={gameActions.resetGame} />
            ) : (
              <GameInterface
                gameState={{
                  gamePhase,
                  categories,
                  selectedCategories,
                  selectedDifficulty,
                  currentQuestion,
                  shuffledQuestions,
                }}
                gameActions={gameActions}
                handleAnswer={handleAnswer}
              />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
